import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Users, Gavel, DollarSign, Trophy, PackageOpen, Pencil, Upload, X, Trash2, ListChecks, Pause, Play, Landmark } from "lucide-react";

const EMPTY_FORM = {
  title: "", description: "", image_url: "", retail_price: "",
  source: "manual", asin: "", source_url: "",
};

export default function Admin() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("manual"); // manual | amazon
  const [form, setForm] = useState(EMPTY_FORM);
  const [creating, setCreating] = useState(false);

  // Amazon import state
  const [amzInput, setAmzInput] = useState("");
  const [fetching, setFetching] = useState(false);

  // Image upload
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  // Auction list
  const [auctions, setAuctions] = useState([]);
  const [deletingId, setDeletingId] = useState(null);

  const loadAuctions = () =>
    api.get("/admin/auctions").then(({ data }) => setAuctions(data.auctions)).catch(() => {});

  useEffect(() => {
    loadAuctions();
  }, []);

  const scrollToAuctions = () => {
    const el = document.getElementById("admin-auctions-section");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5 MB");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/uploads/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image_url: data.image_url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const load = () =>
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  if (!user || user.role !== "admin") {
    return (
      <div className="max-w-7xl mx-auto px-6 py-16 text-zinc-400">
        Admin only. Log in as admin.
      </div>
    );
  }

  const fetchFromAmazon = async (e) => {
    e.preventDefault();
    setFetching(true);
    try {
      const { data } = await api.post("/admin/amazon/fetch", { url_or_asin: amzInput });
      setForm({
        title: data.title || "",
        description: data.title ? `${data.title} — imported from Amazon.` : "",
        image_url: data.image_url || "",
        retail_price: data.retail_price != null ? String(data.retail_price) : "",
        source: "amazon",
        asin: data.asin,
        source_url: data.source_url,
      });
      if (data.scraped) {
        toast.success(`Imported ASIN ${data.asin}. Review and confirm.`);
      } else {
        toast.info(`ASIN ${data.asin} detected. Fill in the fields manually (Amazon blocks direct scraping).`);
      }
      setTab("manual"); // move to form to complete/edit
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not import");
    } finally {
      setFetching(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.image_url) {
      toast.error("Please upload or paste a product image");
      return;
    }
    setCreating(true);
    try {
      await api.post("/admin/auctions", {
        title: form.title,
        description: form.description,
        image_url: form.image_url,
        retail_price: parseFloat(form.retail_price),
        source: form.source,
        asin: form.asin || null,
        source_url: form.source_url || null,
      });
      toast.success("Auction created");
      setForm(EMPTY_FORM);
      setAmzInput("");
      load();
      loadAuctions();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setCreating(false);
    }
  };

  const deleteAuction = async (auction) => {
    const bidsNote = auction.bid_count > 0 ? ` and ${auction.bid_count} bid(s)` : "";
    if (!window.confirm(`Delete "${auction.title}"${bidsNote}? This cannot be undone.`)) return;
    setDeletingId(auction.id);
    try {
      await api.delete(`/admin/auctions/${auction.id}`);
      toast.success("Auction removed");
      setAuctions((list) => list.filter((a) => a.id !== auction.id));
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  const stopAuction = async (auction) => {
    if (!window.confirm(`Stop "${auction.title}" now? The last bidder (${auction.last_bidder_name || "no one yet"}) will win at $${auction.current_price.toFixed(2)}.`)) return;
    setDeletingId(auction.id);
    try {
      const { data } = await api.post(`/admin/auctions/${auction.id}/stop`);
      toast.success("Auction stopped");
      setAuctions((list) => list.map((a) => (a.id === auction.id ? data.auction : a)));
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Stop failed");
    } finally {
      setDeletingId(null);
    }
  };

  const restartAuction = async (auction) => {
    if (!window.confirm(`Restart "${auction.title}"? Price resets to $0.00, bid history clears, and the 24h timer starts over.`)) return;
    setDeletingId(auction.id);
    try {
      const { data } = await api.post(`/admin/auctions/${auction.id}/restart`);
      toast.success("Auction restarted");
      setAuctions((list) => list.map((a) => (a.id === auction.id ? data.auction : a)));
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Restart failed");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="grid-bg min-h-[calc(100vh-73px)]">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="flex items-start justify-between gap-6 mb-10 flex-wrap">
          <div>
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tighter mb-2">
              Admin Console
            </h1>
            <p className="text-zinc-500">Manage auctions and watch the numbers roll in.</p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/admin/payments"
              data-testid="bank-payments-link"
              className="btn-outline text-sm !px-5 flex items-center gap-2 border-[#22C55E]/40 text-[#22C55E] hover:bg-[#22C55E]/10"
            >
              <Landmark size={16} />
              Bank &amp; Payments
            </Link>
            <button
              onClick={scrollToAuctions}
              data-testid="manage-auctions-button"
              className="btn-bid text-sm !px-5 flex items-center gap-2"
            >
              <ListChecks size={16} strokeWidth={3} />
              Manage auctions ({auctions.length})
            </button>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-12">
            <StatCard icon={<Users />} label="Users" value={stats.total_users} />
            <StatCard icon={<Gavel />} label="Live" value={stats.live_auctions} />
            <StatCard icon={<Trophy />} label="Closed" value={stats.closed_auctions} />
            <StatCard icon={<Gavel />} label="Bids" value={stats.total_bids} />
            <StatCard icon={<DollarSign />} label="Revenue" value={`$${stats.revenue_usd}`} />
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          <div className="card-surface p-6">
            {/* Tabs */}
            <div className="flex items-center gap-2 mb-6 border-b border-white/5 -mx-6 px-6 pb-3">
              <TabButton active={tab === "amazon"} onClick={() => setTab("amazon")} icon={<PackageOpen size={14} />} label="Import from Amazon" testId="tab-amazon" />
              <TabButton active={tab === "manual"} onClick={() => setTab("manual")} icon={<Pencil size={14} />} label="Create manually" testId="tab-manual" />
            </div>

            {tab === "amazon" ? (
              <form onSubmit={fetchFromAmazon} className="space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-widest text-zinc-500 mb-2 block">
                    Amazon URL or ASIN
                  </label>
                  <input
                    type="text"
                    required
                    className="input-field font-mono-nums"
                    placeholder="https://www.amazon.com/dp/B08N5WRWNW"
                    value={amzInput}
                    onChange={(e) => setAmzInput(e.target.value)}
                    data-testid="admin-amazon-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={fetching}
                  className="btn-bid w-full text-sm"
                  data-testid="admin-amazon-fetch"
                >
                  {fetching ? "Importing…" : "Import product"}
                </button>
                <div className="text-xs text-zinc-500 leading-relaxed">
                  We extract the ASIN and try to prefill fields. Amazon aggressively blocks
                  automated scraping, so you may need to paste the title, image URL and retail
                  price manually. The imported auction keeps a link back to the Amazon listing
                  for fulfilment later.
                </div>
              </form>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="admin-create-form">
                {form.asin && (
                  <div className="chip chip-live">Imported from Amazon · ASIN {form.asin}</div>
                )}
                {[
                  ["title", "Title", "text"],
                ].map(([k, label, type]) => (
                  <div key={k}>
                    <label className="text-xs uppercase tracking-widest text-zinc-500 mb-2 block">
                      {label}
                    </label>
                    <input
                      type={type}
                      required
                      className="input-field"
                      value={form[k]}
                      onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                      data-testid={`admin-input-${k}`}
                    />
                  </div>
                ))}

                {/* Image: upload OR paste URL */}
                <div>
                  <label className="text-xs uppercase tracking-widest text-zinc-500 mb-2 block">
                    Product image
                  </label>
                  {form.image_url ? (
                    <div className="relative inline-block">
                      <img
                        src={form.image_url}
                        alt="preview"
                        className="w-32 h-32 object-cover rounded-lg border border-white/10"
                        data-testid="admin-image-preview"
                      />
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, image_url: "" })}
                        className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-[#1A1A24] border border-white/10 flex items-center justify-center text-zinc-400 hover:text-white"
                        data-testid="admin-image-clear"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="btn-outline w-full text-sm flex items-center justify-center gap-2"
                        data-testid="admin-image-upload-button"
                      >
                        <Upload size={14} />
                        {uploading ? "Uploading…" : "Upload image (jpg/png/webp, max 5 MB)"}
                      </button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        className="hidden"
                        onChange={onPickImage}
                        data-testid="admin-image-file-input"
                      />
                      <input
                        type="url"
                        className="input-field text-xs"
                        placeholder="…or paste an image URL"
                        value={form.image_url}
                        onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                        data-testid="admin-input-image_url"
                      />
                    </div>
                  )}
                </div>

                {[
                  ["retail_price", "Retail price (USD)", "number"],
                ].map(([k, label, type]) => (
                  <div key={k}>
                    <label className="text-xs uppercase tracking-widest text-zinc-500 mb-2 block">
                      {label}
                    </label>
                    <input
                      type={type}
                      required
                      step={type === "number" ? "0.01" : undefined}
                      className="input-field"
                      value={form[k]}
                      onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                      data-testid={`admin-input-${k}`}
                    />
                  </div>
                ))}
                <div>
                  <label className="text-xs uppercase tracking-widest text-zinc-500 mb-2 block">
                    Description
                  </label>
                  <textarea
                    required
                    rows={3}
                    className="input-field resize-none"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    data-testid="admin-input-description"
                  />
                </div>
                <button disabled={creating} className="btn-bid w-full text-sm" data-testid="admin-create-button">
                  {creating ? "Creating…" : "Create auction"}
                </button>
              </form>
            )}
          </div>

          <div className="card-surface p-6">
            <h2 className="font-display font-bold text-xl mb-4">How it works</h2>
            <ul className="text-sm text-zinc-400 space-y-2 list-disc pl-5">
              <li>Each bid deducts 1 token ($0.50) from the bidder.</li>
              <li>Each bid raises current price by $0.01 and resets the timer to 30s.</li>
              <li><span className="text-[#FBBF24]">Auctions close ONLY when platform profit reaches $500</span> — if no new bid arrives, the 30s timer auto-renews so the auction keeps going until the threshold is hit.</li>
              <li>The last bidder when $500 profit is hit wins the item at the final displayed price.</li>
              <li>Profit = (bid count × $0.50) − retail price.</li>
              <li>Winners are shipped to the address they save under <span className="font-medium">Shipping</span>. Amazon-imported auctions keep the source URL for fulfilment.</li>
            </ul>
          </div>
        </div>

        {/* Auctions list — manage existing items */}
        <section
          id="admin-auctions-section"
          className="mt-10 card-surface p-6 scroll-mt-24"
          style={{ border: "1.5px solid rgba(251,191,36,0.35)", boxShadow: "0 0 40px -20px rgba(251,191,36,0.4)" }}
          data-testid="manage-auctions-section"
        >
          <div className="flex items-end justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#FBBF24] flex items-center justify-center">
                <ListChecks size={18} className="text-black" strokeWidth={3} />
              </div>
              <div>
                <h2 className="font-display font-bold text-2xl tracking-tighter">Your auctions</h2>
                <p className="text-zinc-500 text-sm mt-0.5">
                  {auctions.length} total · each row has{" "}
                  <span className="text-[#FBBF24] font-semibold">Stop</span> /
                  <span className="text-[#22C55E] font-semibold"> Restart</span> /
                  <span className="text-[#EF4444] font-semibold"> Delete</span> buttons on the right.
                </p>
              </div>
            </div>
            <button onClick={loadAuctions} className="btn-outline text-xs" data-testid="refresh-auctions">
              Refresh
            </button>
          </div>

          {auctions.length === 0 ? (
            <div className="p-10 text-center text-zinc-500 text-sm">
              No auctions yet. Create one above to get started.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-white/5" data-testid="admin-auctions-table">
              <table className="w-full text-sm min-w-[800px]">
                <thead className="bg-white/5">
                  <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500">
                    <th className="py-3 px-4">Item</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4">Current</th>
                    <th className="py-3 px-4">Bids</th>
                    <th className="py-3 px-4">Profit</th>
                    <th className="py-3 px-4 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {auctions.map((a) => (
                    <tr
                      key={a.id}
                      className="border-t border-white/5"
                      data-testid={`admin-auction-row-${a.id}`}
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <img
                            src={a.image_url}
                            alt=""
                            className="w-10 h-10 rounded object-cover border border-white/10"
                          />
                          <div>
                            <div className="font-medium">{a.title}</div>
                            <div className="text-[11px] text-zinc-500 font-mono-nums">
                              retail ${a.retail_price.toFixed(2)}
                              {a.asin && ` · ASIN ${a.asin}`}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`chip ${a.status === "live" ? "chip-live" : "chip-closed"}`}>
                          {a.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 font-mono-nums text-[#FBBF24]">
                        ${a.current_price.toFixed(2)}
                      </td>
                      <td className="py-3 px-4 font-mono-nums">{a.bid_count}</td>
                      <td className="py-3 px-4 font-mono-nums text-xs">
                        ${a.profit?.toFixed(2) ?? "0.00"}
                        <span className="text-zinc-600"> / $500</span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {a.status === "live" ? (
                            <button
                              onClick={() => stopAuction(a)}
                              disabled={deletingId === a.id}
                              data-testid={`stop-auction-${a.id}`}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#FBBF24] text-black font-bold hover:bg-[#ffd043] disabled:opacity-50 transition text-xs shadow-[0_0_12px_-4px_#FBBF24]"
                              title="Stop this auction now (last bidder wins)"
                            >
                              <Pause size={12} strokeWidth={3} /> Stop
                            </button>
                          ) : (
                            <button
                              onClick={() => restartAuction(a)}
                              disabled={deletingId === a.id}
                              data-testid={`restart-auction-${a.id}`}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#22C55E] text-black font-bold hover:bg-[#4ade80] disabled:opacity-50 transition text-xs shadow-[0_0_12px_-4px_#22C55E]"
                              title="Restart this auction with a fresh timer"
                            >
                              <Play size={12} strokeWidth={3} /> Restart
                            </button>
                          )}
                          <button
                            onClick={() => deleteAuction(a)}
                            disabled={deletingId === a.id}
                            data-testid={`delete-auction-${a.id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/30 hover:bg-[#EF4444] hover:text-white transition text-xs font-semibold"
                            title="Permanently delete this auction"
                          >
                            <Trash2 size={12} />
                            {deletingId === a.id ? "…" : "Delete"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function TabButton({ active, onClick, icon, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition ${
        active ? "bg-[#FBBF24] text-black" : "text-zinc-400 hover:text-white"
      }`}
    >
      {icon} {label}
    </button>
  );
}

function StatCard({ icon, label, value }) {
  return (
    <div className="card-surface p-4">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
        <span className="text-[#FBBF24]">{icon}</span> {label}
      </div>
      <div className="font-mono-nums font-bold text-2xl">{value}</div>
    </div>
  );
}
